import moment, { Moment } from 'moment';
import { Field } from 'api/tasks';
import { unreachable } from 'utils/unreachable';
import {
  DateFieldFilter,
  DateFieldFilterOperationType,
  RelativeDirection,
} from '../filters/date';
import { getFieldValue } from './get-field-value';
import { FilterFunction } from '.';

export const date = (filter: DateFieldFilter): FilterFunction => {
  const value = getFieldValue<number | string>(filter.field as any as Field);
  const moment_value = (record: any): Moment | null => {
    const field_value = value(record);

    return field_value == null ? null : moment(field_value);
  };

  switch (filter.type) {
    case DateFieldFilterOperationType.IsEmpty:
      return (record) => value(record) == null;
    case DateFieldFilterOperationType.IsNotEmpty:
      return (record) => value(record) != null;
    case DateFieldFilterOperationType.IsBeforeToday:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.diff(moment(), 'day') < 0;
      };
    case DateFieldFilterOperationType.IsToday:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment(), 'day');
      };
    case DateFieldFilterOperationType.IsTomorrow:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment().add(1, 'day'), 'day');
      };
    case DateFieldFilterOperationType.IsThisWeek:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment(), 'week');
      };
    case DateFieldFilterOperationType.IsLastWeek:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment().subtract(1, 'week'), 'week');
      };
    case DateFieldFilterOperationType.IsNextWeek:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment().add(1, 'week'), 'week');
      };
    case DateFieldFilterOperationType.IsThisMonth:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment(), 'month');
      };
    case DateFieldFilterOperationType.IsLastMonth:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment().subtract(1, 'month'), 'month');
      };
    case DateFieldFilterOperationType.IsThisQuarter:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment(), 'quarter');
      };
    case DateFieldFilterOperationType.IsLastQuarter:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment().subtract(1, 'quarter'), 'quarter');
      };
    case DateFieldFilterOperationType.IsThisYear:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null) {
          return false;
        }

        return date_value.isSame(moment(), 'year');
      };
    case DateFieldFilterOperationType.IsMoreThan:
      return (record) => {
        const date_value = moment_value(record);

        if (
          date_value == null ||
          filter.days == null ||
          filter.direction == null
        ) {
          return false;
        }
        switch (filter.direction) {
          case RelativeDirection.Ago:
            return date_value.isBefore(
              moment().subtract(filter.days, 'days'),
              'day',
            );
          case RelativeDirection.FromNow:
            return date_value.isAfter(moment().add(filter.days, 'days'), 'day');
          default:
            return unreachable(filter.direction);
        }
      };
    case DateFieldFilterOperationType.IsExactly:
      return (record) => {
        const date_value = moment_value(record);

        if (
          date_value == null ||
          filter.days == null ||
          filter.direction == null
        ) {
          return false;
        }
        switch (filter.direction) {
          case RelativeDirection.Ago:
            return date_value.isSame(
              moment().subtract(filter.days, 'days'),
              'day',
            );
          case RelativeDirection.FromNow:
            return date_value.isSame(moment().add(filter.days, 'days'), 'day');
          default:
            return unreachable(filter.direction);
        }
      };
    case DateFieldFilterOperationType.IsLessThan:
      return (record) => {
        const date_value = moment_value(record);

        if (
          date_value == null ||
          filter.days == null ||
          filter.direction == null
        ) {
          return false;
        }
        switch (filter.direction) {
          case RelativeDirection.Ago:
            return date_value.isAfter(
              moment().subtract(filter.days, 'days'),
              'day',
            );
          case RelativeDirection.FromNow:
            return date_value.isBefore(
              moment().add(filter.days, 'days'),
              'day',
            );
          default:
            return unreachable(filter.direction);
        }
      };
    case DateFieldFilterOperationType.IsAfter:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null || filter.value == null) {
          return false;
        }

        return date_value.isAfter(filter.value, 'day');
      };
    case DateFieldFilterOperationType.IsOn:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null || filter.value == null) {
          return false;
        }

        return date_value.isSame(filter.value, 'day');
      };
    case DateFieldFilterOperationType.IsBefore:
      return (record) => {
        const date_value = moment_value(record);

        if (date_value == null || filter.value == null) {
          return false;
        }

        return date_value.isBefore(filter.value, 'day');
      };
    case DateFieldFilterOperationType.IsAny:
      return () => true;
    default:
      return unreachable(filter);
  }
};
